import _, { isNumber } from 'lodash';
import { areRadiusEqual } from '@/utils/math-kit';
import { traverseTree } from '@/utils/tree-kit';
import { DEFAULT_DPI, SizeUnitType } from '@/utils/unit-kit';
import {
    BrushType,
    DynamicType,
    IBackgroundGradientProps,
    IFontBackground,
    IImageEffectShadowType,
    ILinearGradient,
    ImgCategoryType,
    ImgModeType,
    LayerType,
    MarkupType,
    StretchOptionType,
    TextAlignType,
    TextShapeType,
} from '@/version-types';
import { ITopStruct } from '../server-template-types';
import {
    checkServerTableLayerPropsIsVersion15_0_0,
    IServerBaseLayerProps,
    IServerBgLayerProps,
    IServerBrushLayerProps,
    IServerChartLayerProps,
    IServerGroupLayerProps,
    IServerImageLayerProps,
    IServerLayerProps,
    IServerMarkupLayerProps,
    IServerTextLayerProps,
    IServerVectorLayerProps,
    IServerWatermarkLayerProps,
} from '../server-template-types/layer';
import { VERSION, VERSION_LIST } from '../versions';

const hasUseV1 = function (template: IServerLayerProps[]) {
    return true;
};

const hasUseV2 = function (layers: IServerLayerProps[]) {
    return true;
};
const hasUseV2_1 = function (layers: IServerLayerProps[]): boolean {
    return true;
};
const hasUseV3 = function (layers: IServerLayerProps[]): boolean {
    return true;
};
const hasUseV3_1 = function (layers: IServerLayerProps[]): boolean {
    return true;
};

const hasUseV4 = function (layers: IServerLayerProps[]): boolean {
    return true;
};

const hasUseV4_1 = function (layers: IServerLayerProps[]): boolean {
    return true;
};

const hasUseV5 = function (layers: IServerLayerProps[]): boolean {
    return true;
};

const hasUseV5_1 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (layer.layerType === LayerType.IMAGE_LAYER && (layer as IServerImageLayerProps).filter) {
                useFunc = true;
            }
        });

        return useFunc;
    });

    if (!flag) {
        layers.forEach((topLayer: Partial<IServerLayerProps>) => {
            traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
                if (layer.layerType === LayerType.IMAGE_LAYER) {
                    delete (layer as IServerImageLayerProps).filter;
                }
            });
        });
    }

    return flag;
};

const hasUseV5_2 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (
                layer.layerType === LayerType.IMAGE_LAYER &&
                (layer as IServerImageLayerProps).stretchOption === StretchOptionType.repeat
            ) {
                useFunc = true;
            }

            if (
                layer.layerType === LayerType.BACKGROUND_LAYER &&
                (layer as IServerBgLayerProps).stretchOption === StretchOptionType.repeat
            ) {
                useFunc = true;
            }
        });

        return useFunc;
    });

    return flag;
};

const hasUseV6 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (layer.layerType === LayerType.WATERMARK) {
                useFunc = true;
            }
        });

        return useFunc;
    });

    return flag;
};

const hasUseV7 = function (layers: IServerLayerProps[]): boolean {
    return false;
};

const hasUseV7_1 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (layer.layerType === LayerType.IMAGE_LAYER && (layer as IServerImageLayerProps).effect) {
                useFunc = true;
            }
        });

        return useFunc;
    });

    if (!flag) {
        layers.forEach((topLayer: Partial<IServerLayerProps>) => {
            traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
                if (layer.layerType === LayerType.IMAGE_LAYER) {
                    delete (layer as IServerImageLayerProps).effect;
                }
            });
        });
    }

    return flag;
};

const hasUseV8 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (layer.layerType === LayerType.GROUP_LAYER && (layer as IServerGroupLayerProps).isTextMaterial) {
                useFunc = true;
            }
        });

        return useFunc;
    });

    return flag;
};

const hasUseV8_1 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (
                layer.layerType === LayerType.TEXT_LAYER &&
                (layer as IServerTextLayerProps).textGradient &&
                ((layer as IServerTextLayerProps).textGradient as any).colors.length > 0
            ) {
                useFunc = true;
            }

            if (
                layer.layerType === LayerType.IMAGE_LAYER &&
                (layer as IServerImageLayerProps).category === ImgCategoryType.qrCode
            ) {
                useFunc = true;
            }
        });

        return useFunc;
    });

    if (!flag) {
        layers.forEach((topLayer: Partial<IServerLayerProps>) => {
            traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
                if (layer.layerType === LayerType.TEXT_LAYER) {
                    delete (layer as IServerTextLayerProps).textGradient;
                }
            });
        });
    }

    return flag;
};

const hasUseV8_2 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (
                layer.layerType === LayerType.BACKGROUND_LAYER &&
                (layer as IServerBgLayerProps).backgroundGradient &&
                ((layer as IServerBgLayerProps).backgroundGradient as IBackgroundGradientProps).colors.length > 0
            ) {
                useFunc = true;
            }
        });

        return useFunc;
    });

    if (!flag) {
        layers.forEach((topLayer: Partial<IServerLayerProps>) => {
            traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
                if (layer.layerType === LayerType.BACKGROUND_LAYER) {
                    delete (layer as IServerBgLayerProps).backgroundGradient;
                }
            });
        });
    }

    return flag;
};

const hasUseV8_3 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (
                layer.layerType === LayerType.TEXT_LAYER &&
                (layer as IServerTextLayerProps).textBackground &&
                ((layer as IServerTextLayerProps).textBackground as IFontBackground[]).length > 0
            ) {
                useFunc = true;
            }
        });

        return useFunc;
    });

    if (!flag) {
        layers.forEach((topLayer: Partial<IServerLayerProps>) => {
            traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
                if (layer.layerType === LayerType.TEXT_LAYER) {
                    delete (layer as IServerTextLayerProps).textBackground;
                }
            });
        });
    }

    return flag;
};

const hasUseV8_4 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (
                layer.layerType === LayerType.TEXT_LAYER &&
                (layer as IServerTextLayerProps).listStyle &&
                (layer as IServerTextLayerProps).listStyle !== ''
            ) {
                useFunc = true;
            }
        });

        return useFunc;
    });

    if (!flag) {
        layers.forEach((topLayer: Partial<IServerLayerProps>) => {
            traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
                if (layer.layerType === LayerType.TEXT_LAYER) {
                    delete (layer as IServerTextLayerProps).listStyle;
                }
            });
        });
    }

    return flag;
};

const hasUseV8_5 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (
                layer.layerType === LayerType.TEXT_LAYER &&
                (layer as IServerTextLayerProps).textAlign === TextAlignType.JUSTIFY
            ) {
                useFunc = true;
            }
        });

        return useFunc;
    });

    return flag;
};

const hasUseV9 = function (layers: IServerLayerProps[], is900Version: boolean): boolean {
    if (is900Version) {
        return true;
    }

    return false;
};

const hasUseV9_1 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (layer.prompt) {
                useFunc = true;
            }
        });

        return useFunc;
    });

    if (!flag) {
        layers.forEach((topLayer: Partial<IServerLayerProps>) => {
            traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
                delete layer.prompt;
            });
        });
    }

    return flag;
};

const hasUseV10 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (
                (layer.materialIds && layer.materialIds['func-list']) ||
                (layer.materialIds && layer.materialIds['text-effect'])
            ) {
                useFunc = true;
            }
        });

        return useFunc;
    });

    return flag;
};

const hasUseV11 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (layer.materialIds && layer.materialIds['photo-material']) {
                useFunc = true;
            }
        });

        return useFunc;
    });

    return flag;
};

const hasUseV11_1 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (layer.isAILayer) {
                useFunc = true;
            }
        });

        return useFunc;
    });

    if (!flag) {
        layers.forEach((topLayer: Partial<IServerLayerProps>) => {
            traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
                (layer as IServerBaseLayerProps).isAILayer !== undefined &&
                    delete (layer as IServerBaseLayerProps).isAILayer;
            });
        });
    }

    return flag;
};

const hasUseV12 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            // 应用了素材 svg
            if (layer.layerType === LayerType.VECTOR_LAYER && layer.materialIds?.['vector-material']?.length) {
                useFunc = true;
            }
        });

        return useFunc;
    });

    return flag;
};

const hasUseV12_1 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (layer.layerType === LayerType.IMAGE_LAYER) {
                const serverLayer = layer as IServerImageLayerProps;
                if (
                    serverLayer.hint ||
                    serverLayer.mode === ImgModeType.matting ||
                    serverLayer.category === ImgCategoryType.qrCode ||
                    serverLayer.category === ImgCategoryType.mainImg ||
                    serverLayer.category === ImgCategoryType.subImg
                ) {
                    useFunc = true;
                }
            }
        });

        return useFunc;
    });

    if (!flag) {
        layers.forEach((topLayer: Partial<IServerLayerProps>) => {
            traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
                delete (layer as IServerImageLayerProps).hint;
            });
        });
    }
    return flag;
};

const hasUseV12_2 = function (layers: IServerLayerProps[], is900Version: boolean, topStruct: ITopStruct): boolean {
    const flag = !!topStruct.unit && topStruct.unit !== SizeUnitType.px;
    return flag;
};

const hasUseV13 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            // 应用了 psd 解析 svg
            if (layer.layerType === LayerType.VECTOR_LAYER && !layer.materialIds?.['vector-material']?.length) {
                useFunc = true;
            }
        });

        return useFunc;
    });

    return flag;
};

const hasUseV13_1 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (layer.layerType === LayerType.TEXT_LAYER) {
                const serverLayer = layer as IServerTextLayerProps;
                if (serverLayer.textContents?.length) {
                    useFunc = true;
                }
            }
        });

        return useFunc;
    });

    if (!flag) {
        layers.forEach((topLayer: Partial<IServerLayerProps>) => {
            traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
                delete (layer as IServerTextLayerProps).textContents;
            });
        });
    }

    return flag;
};

const hasUseV13_2 = function (layers: IServerLayerProps[], is900Version: boolean, topStruct: ITopStruct): boolean {
    const flag = !!topStruct.resolution && topStruct.resolution !== DEFAULT_DPI;
    return flag;
};

const hasUseV13_3 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (
                (layer.layerType === LayerType.VECTOR_LAYER &&
                    (layer as IServerVectorLayerProps).pickerIndex !== undefined &&
                    layer.layerType === LayerType.VECTOR_LAYER &&
                    (layer as IServerVectorLayerProps).pickerIndex !== null) ||
                (layer.layerType === LayerType.IMAGE_LAYER &&
                    ((layer as IServerImageLayerProps).category === ImgCategoryType.mainImg ||
                        (layer as IServerImageLayerProps).category === ImgCategoryType.subImg))
            ) {
                useFunc = true;
            }
        });

        return useFunc;
    });

    if (!flag) {
        layers.forEach((topLayer: Partial<IServerLayerProps>) => {
            traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
                if (layer.layerType === LayerType.VECTOR_LAYER) {
                    delete (layer as IServerVectorLayerProps).pickerIndex;
                }
            });
        });
    }

    return flag;
};

const hasUseV13_1_0 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (layer.layerType === LayerType.QR_CODE) {
                useFunc = true;
            }
        });

        return useFunc;
    });

    return flag;
};

const hasUseV14 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (layer.layerType === LayerType.IMAGE_LAYER) {
                const serverLayer = layer as IServerImageLayerProps;
                if (serverLayer.aiTextExtension) {
                    useFunc = true;
                }
            }
        });

        return useFunc;
    });

    if (!flag) {
        layers.forEach((topLayer: Partial<IServerLayerProps>) => {
            traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
                delete (layer as IServerImageLayerProps).aiTextExtension;
            });
        });
    }
    return flag;
};

const hasUseV14_1_0 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (layer.layerType === LayerType.TEXT_LAYER) {
                const serverLayer = layer as IServerTextLayerProps;
                if (serverLayer.shapeExtension && serverLayer.screenshotUrl) {
                    useFunc = true;
                }
            }
        });

        return useFunc;
    });

    if (!flag) {
        layers.forEach((topLayer: Partial<IServerLayerProps>) => {
            traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
                delete (layer as IServerTextLayerProps).shapeExtension;
                delete (layer as IServerTextLayerProps).screenshotUrl;
                delete layer.materialIds?.['text-transform'];
            });
        });
    }
    return flag;
};

const hasUseV14_2_0 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (layer.layerType === LayerType.IMAGE_LAYER) {
                const serverLayer = layer as IServerImageLayerProps;
                if (serverLayer.effect) {
                    useFunc = true;
                }
            }
        });

        return useFunc;
    });

    if (!flag) {
        layers.forEach((topLayer: Partial<IServerLayerProps>) => {
            traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
                if (layer.layerType === LayerType.IMAGE_LAYER) {
                    (layer as IServerImageLayerProps).effect = null;
                }
            });
        });
    }

    return flag;
};

const hasUseV15 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (layer.layerType === LayerType.TABLE) {
                useFunc = true;
            }
        });

        return useFunc;
    });

    return flag;
};

const hasUseV15_1 = function (layers: IServerLayerProps[]): boolean {
    layers.find((topLayer) => {
        const useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            delete layer.fitWidth;
            delete layer.fitHeight;
        });

        return useFunc;
    });

    return false;
};

const hasUseV15_1_1 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (layer.layerType === LayerType.BRUSH_LAYER) {
                useFunc = true;
            }
        });

        return useFunc;
    });

    return flag;
};

const hasUseV15_1_2 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (layer.layerType === LayerType.TABLE && !checkServerTableLayerPropsIsVersion15_0_0(layer)) {
                useFunc = true;
            }
        });

        return useFunc;
    });

    return flag;
};

const hasUseV15_2_0 = function (layers: IServerLayerProps[]): boolean {
    // 该版本修改蒙版变形方式， 配方新增minimumMask，但不强制升级配方版本
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (
                layer.layerType === LayerType.IMAGE_LAYER &&
                ((layer as IServerImageLayerProps).maskFeathering || (layer as IServerImageLayerProps).minimumMask)
            ) {
                useFunc = true;
            }
        });

        return useFunc;
    });

    if (!flag) {
        layers.forEach((topLayer: Partial<IServerLayerProps>) => {
            traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
                if (layer.layerType === LayerType.IMAGE_LAYER) {
                    delete (layer as IServerImageLayerProps).maskFeathering;
                    delete (layer as IServerImageLayerProps).minimumMask;
                }
            });
        });
    }

    return flag;
};

const hasUseV15_3_0 = function (layers: IServerLayerProps[]): boolean {
    // 该版本修改蒙版变形方式， 配方新增minimumMask，但不强制升级配方版本
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (
                layer.layerType === LayerType.IMAGE_LAYER &&
                (layer as IServerImageLayerProps).blendMode !== undefined
            ) {
                useFunc = true;
            }
        });

        return useFunc;
    });

    if (!flag) {
        layers.forEach((topLayer: Partial<IServerLayerProps>) => {
            traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
                if (layer.layerType === LayerType.IMAGE_LAYER) {
                    delete (layer as IServerImageLayerProps).blendMode;
                }
            });
        });
    }

    return flag;
};

const hasUseV16_0_0 = (layers: IServerLayerProps[]) => {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (layer.layerType === LayerType.MAGNIFIER) {
                useFunc = true;
            }
        });

        return useFunc;
    });

    return flag;
};

const hasUseV17_0_0 = (layers: IServerLayerProps[]) => {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (layer.layerType === LayerType.PUZZLE || layer.layerType === LayerType.PUZZLE_CONTAINER) {
                useFunc = true;
            }
        });

        return useFunc;
    });

    return flag;
};

const hasUseV17_0_1 = (layers: IServerLayerProps[]) => {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (
                layer.layerType === LayerType.BRUSH_LAYER &&
                (layer as IServerBrushLayerProps).brushType === BrushType.Wavy
            ) {
                useFunc = true;
            }
        });

        return useFunc;
    });

    return flag;
};

const hasUseV17_0_2 = (layers: IServerLayerProps[]) => {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (layer.extraInfo) {
                useFunc = true;
            }
        });

        return useFunc;
    });

    if (!flag) {
        layers.forEach((topLayer: Partial<IServerLayerProps>) => {
            traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
                delete layer.extraInfo;
            });
        });
    }

    return flag;
};

const hasUseV17_0_3 = (layers: IServerLayerProps[]) => {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (layer.layerType === LayerType.TEXT_LAYER) {
                const textBackground = (layer as IServerTextLayerProps).textBackground;
                if (textBackground?.length) {
                    textBackground.forEach((item) => {
                        if (isNumber(item.roundWeight) && item.roundWeight > 0) {
                            useFunc = true;
                        }
                    });
                }
            }
        });

        return useFunc;
    });

    return flag;
};

const hasUseV17_1_0 = (layers: IServerLayerProps[]) => {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (layer.layerType === LayerType.TEXT_LAYER) {
                const shapeExtension = (layer as IServerTextLayerProps).shapeExtension;
                if (shapeExtension) {
                    const type = shapeExtension.curveTextType;
                    if (type >= TextShapeType.waveShaped && type <= TextShapeType.lowerArc) {
                        useFunc = true;
                    }
                    // if (shapeExtension.relativeHeight !== undefined) {
                    //     useFunc = true;
                    // }
                }
            }
        });

        return useFunc;
    });

    return flag;
};

const hasUseV17_1_1 = (layers: IServerLayerProps[]) => {
    return false;
};

const hasUseV17_1_2 = (layers: IServerLayerProps[]) => {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if ((layer as IServerImageLayerProps).effect?.advancedSetting) {
                useFunc = true;
            }
        });

        return useFunc;
    });

    if (!flag) {
        layers.forEach((topLayer: Partial<IServerLayerProps>) => {
            traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
                if (layer.layerType === LayerType.IMAGE_LAYER && (layer as IServerImageLayerProps).effect) {
                    if (
                        (layer as IServerImageLayerProps).effect &&
                        (layer as IServerImageLayerProps).effect?.advancedSetting
                    ) {
                        delete ((layer as IServerImageLayerProps).effect as any).advancedSetting;
                    }
                }
            });
        });
    }

    return false;
};

const hasUseV17_2_0 = (layers: IServerLayerProps[]) => {
    return false;
};

const hasUseV18_0_0 = (layers: IServerLayerProps[]) => {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (layer.flipH || layer.flipV) {
                useFunc = true;
            }
            if (layer.layerType === LayerType.TEXT_LAYER) {
                if ((layer as IServerTextLayerProps)?.texture?.url) {
                    useFunc = true;
                }
            }
        });

        return useFunc;
    });

    if (!flag) {
        layers.forEach((topLayer: Partial<IServerLayerProps>) => {
            traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
                delete layer.flipH;
                delete layer.flipV;
                delete (layer as IServerTextLayerProps).texture;
            });
        });
    }

    return flag;
};

const hasUseV18_0_1 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (layer.layerType === LayerType.MARKUP_LAYER) {
                useFunc = true;
            }
        });

        return useFunc;
    });

    return flag;
};

const hasUseV18_0_2 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (layer.layerType === LayerType.VECTOR_LAYER) {
                const _layer = layer as IServerVectorLayerProps;
                const { blur, effect } = _layer;
                if (blur || effect?.reflection[0] || effect?.shadow[0] || _layer.strokeHidden) {
                    useFunc = true;
                }
            }
        });

        return useFunc;
    });
    if (!flag) {
        layers.forEach((topLayer: Partial<IServerLayerProps>) => {
            traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
                if (layer.layerType === LayerType.VECTOR_LAYER) {
                    const _layer = layer as IServerVectorLayerProps;
                    delete _layer.strokeHidden;
                    delete _layer.blur;
                    delete _layer.effect;
                }
            });
        });
    }
    return flag;
};

const hasUseV18_1_0 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (
                layer.layerType === LayerType.MARKUP_LAYER &&
                (layer as IServerMarkupLayerProps).markupType === MarkupType.Segment
            ) {
                useFunc = true;
            }
        });

        return useFunc;
    });

    if (!flag) {
        layers.forEach((topLayer: Partial<IServerLayerProps>) => {
            traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
                if (layer.layerType === LayerType.MARKUP_LAYER) {
                    const _layer = layer as IServerMarkupLayerProps;
                    delete _layer.anchor.points;
                }
            });
        });
    }
    return flag;
};

const hasUseV18_1_1 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (layer.layerType === LayerType.IMAGE_LAYER && (layer as IServerImageLayerProps).borderRadius) {
                useFunc = true;
            }
        });

        return useFunc;
    });

    if (!flag) {
        layers.forEach((topLayer: Partial<IServerLayerProps>) => {
            traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
                if (layer.layerType === LayerType.IMAGE_LAYER) {
                    const _layer = layer as IServerImageLayerProps;
                    delete _layer.borderRadius;
                }
            });
        });
    }
    return flag;
};

const hasUseV18_2_0 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (layer.layerType === LayerType.TEXT_LAYER) {
                const serverLayer = layer as IServerTextLayerProps;
                if (serverLayer.textMark) {
                    useFunc = true;
                } else {
                    if (serverLayer.textContents?.find((item) => item.textMark)) {
                        useFunc = true;
                    }
                }
            }
        });

        return useFunc;
    });

    if (!flag) {
        layers.forEach((topLayer: Partial<IServerLayerProps>) => {
            traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
                const serverLayer = layer as IServerTextLayerProps;
                delete serverLayer.textMark;
                serverLayer.textContents?.forEach((item) => {
                    delete item.textMark;
                });
            });
        });
    }

    return flag;
};
const hasUseV18_2_1 = function (layers: IServerLayerProps[]): boolean {
    return false;
};

const hasUseV18_2_2 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            const _layer = layer as IServerVectorLayerProps;
            if (_layer.layerType === LayerType.VECTOR_LAYER && _layer.borderRadius) {
                const { leftBottom, leftTop, rightBottom, rightTop } = _layer.borderRadius;
                if (!areRadiusEqual(leftBottom, leftTop, rightBottom, rightTop)) {
                    useFunc = true;
                }
            }
        });
        return useFunc;
    });

    if (!flag) {
        layers.forEach((topLayer: Partial<IServerLayerProps>) => {
            traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
                const _layer = layer as IServerVectorLayerProps;
                if (_layer.layerType === LayerType.VECTOR_LAYER && _layer.borderRadius) {
                    _layer.radius = _layer.borderRadius.leftTop;
                    delete _layer.borderRadius;
                }
            });
        });
    }
    return flag;
};

const hasUseV18_2_3 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            const _layer = layer as IServerBgLayerProps;
            if (_layer.layerType === LayerType.BACKGROUND_LAYER && _layer.dynamicInfo) {
                useFunc = true;
            }
        });
        return useFunc;
    });

    if (!flag) {
        layers.forEach((topLayer: Partial<IServerLayerProps>) => {
            traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
                const _layer = layer as IServerBgLayerProps;
                if (_layer.layerType === LayerType.BACKGROUND_LAYER) {
                    delete _layer.dynamicInfo;
                }
            });
        });
    }
    return flag;
};

const hasUseV18_4_0 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        const useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            const _layer = layer as IServerTextLayerProps;
            if (_layer.layerType === LayerType.TEXT_LAYER && _layer.textContents && _layer.textContents.length > 0) {
                return _layer.textContents.find((item) => {
                    return (
                        (item.textBackground && item.textBackground.length > 0) ||
                        (item.textStroke && item.textStroke.length > 0) ||
                        (item.textShadow && item.textShadow.length > 0) ||
                        (item.textGlow && item.textGlow.length > 0)
                    );
                });
            }
        });
        return useFunc;
    });

    return flag;
};

const hasUseV18_5_0 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            const _layer = layer as IServerVectorLayerProps;
            if (_layer.layerType === LayerType.VECTOR_LAYER && _layer.textInfo && _layer.textInfo.text !== '') {
                useFunc = true;
            }
        });
        return useFunc;
    });

    if (!flag) {
        layers.forEach((topLayer: Partial<IServerLayerProps>) => {
            traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
                if (layer.layerType === LayerType.VECTOR_LAYER) {
                    delete (layer as IServerVectorLayerProps).textInfo;
                }

                if (layer.layerType === LayerType.TEXT_LAYER) {
                    delete (layer as IServerTextLayerProps).verticalAlign;
                }
            });
        });
    }
    return flag;
};

const hasUseV18_6_0 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            const _layer = layer as IServerImageLayerProps;
            if (_layer.layerType === LayerType.IMAGE_LAYER && _layer.mcpEffects) {
                useFunc = true;
            }
        });
        return useFunc;
    });

    if (!flag) {
        layers.forEach((topLayer: Partial<IServerLayerProps>) => {
            traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
                if (layer.layerType === LayerType.IMAGE_LAYER) {
                    delete (layer as IServerImageLayerProps).mcpEffects;
                }
            });
        });
    }

    return flag;
};

const hasUseV18_7_0 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            const _layer = layer as IServerImageLayerProps;
            if (_layer.layerType === LayerType.IMAGE_LAYER && _layer.fill) {
                useFunc = true;
            }
        });
        return useFunc;
    });

    if (!flag) {
        layers.forEach((topLayer: Partial<IServerLayerProps>) => {
            traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
                const _layer = layer as IServerImageLayerProps;
                if (_layer.layerType === LayerType.IMAGE_LAYER) {
                    delete _layer.fill;
                }
            });
        });
    }
    return flag;
};

const hasUseV19_0_0 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            const _layer = layer as IServerBgLayerProps;
            if (_layer.layerType === LayerType.MEDIA_LAYER) {
                useFunc = true;
            }
        });
        return useFunc;
    });

    return flag;
};

const hasUseV19_1_0 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            const _layer = layer as IServerBgLayerProps;
            if (
                _layer.layerType === LayerType.BACKGROUND_LAYER &&
                _layer.dynamicInfo &&
                (_layer.dynamicInfo.dynamicType === DynamicType.Video ||
                    _layer.dynamicInfo.dynamicType === DynamicType.Webp)
            ) {
                useFunc = true;
            }

            if (layer.materialIds && layer.materialIds['text-animation']) {
                useFunc = true;
            }
        });
        return useFunc;
    });

    return flag;
};

const hasUseV19_2_0 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            const _layer = layer as IServerImageLayerProps;
            if (
                _layer.layerType === LayerType.IMAGE_LAYER &&
                _layer.effect &&
                _layer.effect?.shadow?.length > 0 &&
                (_layer.effect?.shadow?.[0].type !== IImageEffectShadowType.base ||
                    _layer.effect?.shadow?.[0].rotate !== 0)
            ) {
                useFunc = true;
            }
        });
        return useFunc;
    });

    if (!flag) {
        layers.forEach((topLayer: Partial<IServerLayerProps>) => {
            traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
                const _layer = layer as IServerImageLayerProps;
                if (_layer.layerType === LayerType.IMAGE_LAYER && _layer.effect && _layer.effect?.shadow?.length > 0) {
                    delete (_layer.effect?.shadow?.[0] as any).type;
                    delete _layer.effect?.shadow?.[0].rotate;
                }
            });
        });
    }

    return flag;
};

const hasUseV19_3_0 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            const _layer = layer as IServerTextLayerProps;
            if (_layer.layerType === LayerType.TEXT_LAYER && _layer.advancedTextMaterial) {
                useFunc = true;
            }

            if (_layer.layerType === LayerType.TEXT_LAYER && _layer.textContents && _layer.textContents.length > 0) {
                _layer.textContents.forEach((item) => {
                    if (item.rotate || item.scale || item.offsetX || item.offsetY) {
                        useFunc = true;
                    }
                });
            }
        });
        return useFunc;
    });

    if (!flag) {
        layers.forEach((topLayer: Partial<IServerLayerProps>) => {
            traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
                const _layer = layer as IServerTextLayerProps;
                if (_layer.layerType === LayerType.TEXT_LAYER) {
                    delete _layer.advancedTextMaterial;
                }

                if (
                    _layer.layerType === LayerType.TEXT_LAYER &&
                    _layer.textContents &&
                    _layer.textContents.length > 0
                ) {
                    _layer.textContents.forEach((item) => {
                        delete item.rotate;
                        delete item.scale;
                        delete item.offsetX;
                        delete item.offsetY;
                    });
                }
            });
        });
    }

    return flag;
};

const hasUseV19_4_0 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            const _layer = layer as IServerTextLayerProps;
            if (
                _layer.layerType === LayerType.TEXT_LAYER &&
                (_layer.listStyle === 'custom' || _layer.listStyleMaterial)
            ) {
                useFunc = true;
            }
        });
        return useFunc;
    });

    if (!flag) {
        layers.forEach((topLayer: Partial<IServerLayerProps>) => {
            traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
                const _layer = layer as IServerTextLayerProps;
                if (_layer.layerType === LayerType.TEXT_LAYER && _layer.listStyleMaterial) {
                    delete _layer.listStyleMaterial;
                }

                if (
                    _layer.layerType === LayerType.TEXT_LAYER &&
                    _layer.materialIds &&
                    _layer.materialIds['text-list'] &&
                    _layer.materialIds['text-list'].length === 0
                ) {
                    delete _layer.materialIds['text-list'];
                }
            });
        });
    }

    return flag;
};

const hasUseV20_0_0 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            const _layer = layer as IServerChartLayerProps;
            if (_layer.layerType === LayerType.CHART) {
                useFunc = true;
            }
        });
        return useFunc;
    });

    return flag;
};

const hasUseV20_0_1 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            const _layer = layer as IServerWatermarkLayerProps;
            if (_layer.layerType === LayerType.WATERMARK && _layer.isColorApplyToAll) {
                useFunc = true;
            }
        });
        return useFunc;
    });

    if (!flag) {
        layers.forEach((topLayer: Partial<IServerLayerProps>) => {
            traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
                const _layer = layer as IServerWatermarkLayerProps;
                if (_layer.layerType === LayerType.WATERMARK) {
                    delete (_layer as any).isColorApplyToAll;
                }
            });
        });
    }
    return flag;
};

const hasUseV20_0_2 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            const _layer = layer as IServerTextLayerProps;
            if (
                _layer.layerType === LayerType.TEXT_LAYER &&
                _layer.listStyleMaterial &&
                (_layer.listStyleMaterial.fontId || _layer.listStyleMaterial.padZero)
            ) {
                useFunc = true;
            }
        });
        return useFunc;
    });

    if (!flag) {
        layers.forEach((topLayer: Partial<IServerLayerProps>) => {
            traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
                const _layer = layer as IServerTextLayerProps;
                if (
                    _layer.layerType === LayerType.TEXT_LAYER &&
                    _layer.listStyleMaterial &&
                    (_layer.listStyleMaterial.fontId || _layer.listStyleMaterial.padZero)
                ) {
                    delete _layer.listStyleMaterial.fontId;
                    delete _layer.listStyleMaterial.padZero;
                }
            });
        });
    }
    return flag;
};

const hasUseV20_0_3 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;

    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            const _layer = layer as IServerVectorLayerProps;
            if (_layer.layerType === LayerType.VECTOR_LAYER && _layer.textInfo && _layer.textInfo.padding) {
                useFunc = true;
            }
        });
        return useFunc;
    });

    if (!flag) {
        layers.forEach((topLayer: Partial<IServerLayerProps>) => {
            traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
                const _layer = layer as IServerVectorLayerProps;
                if (_layer.layerType === LayerType.VECTOR_LAYER && _layer.textInfo && _layer.textInfo.padding) {
                    delete _layer.textInfo.padding;
                }
            });
        });
    }
    return flag;
};

const hasUseV20_0_4 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;
    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            const _layer = layer as IServerTextLayerProps;
            if (
                _layer.layerType === LayerType.TEXT_LAYER &&
                _layer.listStyleMaterial &&
                _layer.listStyleMaterial.itemSpacing !== undefined
            ) {
                useFunc = true;
            }
        });
        return useFunc;
    });

    if (!flag) {
        layers.forEach((topLayer: Partial<IServerLayerProps>) => {
            traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
                const _layer = layer as IServerTextLayerProps;
                if (
                    _layer.layerType === LayerType.TEXT_LAYER &&
                    _layer.listStyleMaterial &&
                    _layer.listStyleMaterial.itemSpacing !== undefined
                ) {
                    delete _layer.listStyleMaterial.itemSpacing;
                }
            });
        });
    }
    return flag;
};

const hasUseV20_1_0 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;
    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            const _layer = layer as IServerImageLayerProps;
            if (_layer.layerType === LayerType.IMAGE_LAYER && _layer.effect && _layer.effect.effectPosition) {
                useFunc = true;
            }

            if (
                _layer.layerType === LayerType.IMAGE_LAYER &&
                _layer.effect &&
                _layer.effect.shadow?.length > 0 &&
                _layer.effect.shadow[0].distance !== undefined
            ) {
                useFunc = true;
            }
        });
        return useFunc;
    });

    if (!flag) {
        layers.forEach((topLayer: Partial<IServerLayerProps>) => {
            traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
                const _layer = layer as IServerImageLayerProps;
                if (_layer.layerType === LayerType.IMAGE_LAYER && _layer.effect && _layer.effect.effectPosition) {
                    delete _layer.effect.effectPosition;
                }

                if (
                    _layer.layerType === LayerType.IMAGE_LAYER &&
                    _layer.effect &&
                    _layer.effect.shadow?.length > 0 &&
                    _layer.effect.shadow[0].distance
                ) {
                    delete _layer.effect.shadow[0].distance;
                }
            });
        });
    }
    return flag;
};

const hasUseV20_1_1 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;
    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            const _layer = layer as IServerVectorLayerProps;
            if (_layer.layerType === LayerType.VECTOR_LAYER && _layer.fillGradient && _layer.fillGradient.point) {
                useFunc = true;
            }

            if (_layer.layerType === LayerType.VECTOR_LAYER && _layer.strokeGradient && _layer.strokeGradient.point) {
                useFunc = true;
            }

            if (_layer.layerType === LayerType.QR_CODE && _layer.strokeGradient && _layer.strokeGradient.point) {
                useFunc = true;
            }

            if (
                _layer.layerType === LayerType.BACKGROUND_LAYER &&
                (layer as IServerBgLayerProps).backgroundGradient &&
                (layer as IServerBgLayerProps).backgroundGradient?.point
            ) {
                useFunc = true;
            }

            if (
                _layer.layerType === LayerType.BRUSH_LAYER &&
                (layer as IServerBrushLayerProps).fills &&
                (layer as IServerBrushLayerProps).fills.length > 0
            ) {
                const hasUse = (layer as IServerBrushLayerProps).fills.find(
                    (item) => (item.color as ILinearGradient)?.point,
                );
                if (hasUse) {
                    useFunc = true;
                }
            }
        });
        return useFunc;
    });

    return flag;
};

const hasUseV20_2_0 = function (layers: IServerLayerProps[]): boolean {
    let flag = true;
    flag = !!layers.find((topLayer) => {
        let useFunc = false;
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            const _layer = layer as IServerImageLayerProps;
            if (_layer.layerType === LayerType.PUZZLE) {
                useFunc = true;
            }
        });
        return useFunc;
    });

    return flag;
};

const hasUseCurrentVersionMap = {
    [VERSION['1.0.0']]: hasUseV1,
    [VERSION['2.0.0']]: hasUseV2,
    [VERSION['2.0.1']]: hasUseV2_1,
    [VERSION['3.0.0']]: hasUseV3,
    [VERSION['3.1.0']]: hasUseV3_1,
    [VERSION['4.0.0']]: hasUseV4,
    [VERSION['4.1.0']]: hasUseV4_1,
    [VERSION['5.0.0']]: hasUseV5,
    [VERSION['5.1.0']]: hasUseV5_1,
    [VERSION['5.2.0']]: hasUseV5_2,
    [VERSION['6.0.0']]: hasUseV6,
    [VERSION['7.0.0']]: hasUseV7,
    [VERSION['7.1.0']]: hasUseV7_1,
    [VERSION['8.0.0']]: hasUseV8,
    [VERSION['8.1.0']]: hasUseV8_1,
    [VERSION['8.2.0']]: hasUseV8_2,
    [VERSION['8.3.0']]: hasUseV8_3,
    [VERSION['8.4.0']]: hasUseV8_4,
    [VERSION['8.5.0']]: hasUseV8_5,
    [VERSION['9.0.0']]: hasUseV9,
    [VERSION['9.0.1']]: hasUseV9_1,
    [VERSION['10.0.0']]: hasUseV10,
    [VERSION['11.0.0']]: hasUseV11,
    [VERSION['11.1.0']]: hasUseV11_1,
    [VERSION['12.0.0']]: hasUseV12,
    [VERSION['12.0.1']]: hasUseV12_1,
    [VERSION['12.0.2']]: hasUseV12_2,
    [VERSION['13.0.0']]: hasUseV13,
    [VERSION['13.0.1']]: hasUseV13_1,
    [VERSION['13.0.2']]: hasUseV13_2,
    [VERSION['13.0.3']]: hasUseV13_3,
    [VERSION['13.1.0']]: hasUseV13_1_0,
    [VERSION['14.0.0']]: hasUseV14,
    [VERSION['14.1.0']]: hasUseV14_1_0,
    [VERSION['14.2.0']]: hasUseV14_2_0,
    [VERSION['15.0.0']]: hasUseV15,
    [VERSION['15.1.0']]: hasUseV15_1,
    [VERSION['15.1.1']]: hasUseV15_1_1,
    [VERSION['15.1.2']]: hasUseV15_1_2,
    [VERSION['15.2.0']]: hasUseV15_2_0,
    [VERSION['15.3.0']]: hasUseV15_3_0,
    [VERSION['16.0.0']]: hasUseV16_0_0,
    [VERSION['17.0.0']]: hasUseV17_0_0,
    [VERSION['17.0.1']]: hasUseV17_0_1,
    [VERSION['17.0.2']]: hasUseV17_0_2,
    [VERSION['17.0.3']]: hasUseV17_0_3,
    [VERSION['17.1.0']]: hasUseV17_1_0,
    [VERSION['17.1.1']]: hasUseV17_1_1,
    [VERSION['17.1.2']]: hasUseV17_1_2,
    [VERSION['17.2.0']]: hasUseV17_2_0,
    [VERSION['18.0.0']]: hasUseV18_0_0,
    [VERSION['18.0.1']]: hasUseV18_0_1,
    [VERSION['18.0.2']]: hasUseV18_0_2,
    [VERSION['18.1.0']]: hasUseV18_1_0,
    [VERSION['18.1.1']]: hasUseV18_1_1,
    [VERSION['18.2.0']]: hasUseV18_2_0,
    [VERSION['18.2.1']]: hasUseV18_2_1,
    [VERSION['18.2.2']]: hasUseV18_2_2,
    [VERSION['18.2.3']]: hasUseV18_2_3,
    [VERSION['18.4.0']]: hasUseV18_4_0,
    [VERSION['18.5.0']]: hasUseV18_5_0,
    [VERSION['18.6.0']]: hasUseV18_6_0,
    [VERSION['18.7.0']]: hasUseV18_7_0,
    [VERSION['19.0.0']]: hasUseV19_0_0,
    [VERSION['19.1.0']]: hasUseV19_1_0,
    [VERSION['19.2.0']]: hasUseV19_2_0,
    [VERSION['19.3.0']]: hasUseV19_3_0,
    [VERSION['19.4.0']]: hasUseV19_4_0,
    [VERSION['20.0.0']]: hasUseV20_0_0,
    [VERSION['20.0.1']]: hasUseV20_0_1,
    [VERSION['20.0.2']]: hasUseV20_0_2,
    [VERSION['20.0.3']]: hasUseV20_0_3,
    [VERSION['20.0.4']]: hasUseV20_0_4,
    [VERSION['20.1.0']]: hasUseV20_1_0,
    [VERSION['20.1.1']]: hasUseV20_1_1,
    [VERSION['20.2.0']]: hasUseV20_2_0,
};

export const hasUseCurrentVersion = function (
    layers: IServerLayerProps[],
    is900Version: boolean,
    version: VERSION,
    topStruct: ITopStruct,
) {
    return hasUseCurrentVersionMap[version](layers, is900Version, topStruct);
};

export const parseLayersToPreviousVersion = (
    layers: IServerLayerProps[],
    is900Version: boolean,
    version: string,
    topStruct: ITopStruct,
) => {
    let hasVersion = false;
    for (let i = VERSION_LIST.length - 1; i >= 0; i--) {
        if (hasUseCurrentVersion(layers, is900Version, VERSION_LIST[i] as VERSION, topStruct)) {
            version = VERSION_LIST[i];
            hasVersion = true;
            break;
        }
    }

    if (!hasVersion) {
        version = VERSION_LIST[0];
    }

    return {
        layers,
        version,
    };
};

// 强制声明导出 d.ts 文件
export type {};
